import { NOTIFICATION_TYPES } from './constants';

/**
 * Handle medication taken events
 * @param {Object} event - The event with medication details
 * @param {Function} getNotifications - Function to get all notifications
 * @param {Function} markAsRead - Function to mark notifications as read
 * @param {Function} deleteNotification - Function to delete notifications
 * @returns {Promise<void>}
 */
export const handleMedicationTakenEvent = async (event, getNotifications, markAsRead, deleteNotification) => {
  try {
    // console.log('[NotificationService] Handling medication taken event:', event.detail);
    const { userConfigUuid, userId } = event.detail;

    if (!userConfigUuid || !userId) {
      console.warn('[NotificationService] Missing required data in medication taken event');
      return;
    }

    // Find related notifications for this medication
    const notifications = await getNotifications(userId);
    // console.log(`[NotificationService] Found ${notifications.length} total notifications for user ${userId}`);

    const relatedNotifications = notifications.filter(notification => {
      if (notification.isRead || notification.isDeleted) return false;

      try {
        const jsonData = typeof notification.jsonData === 'string'
          ? JSON.parse(notification.jsonData)
          : notification.jsonData;

        // Compare with both string and number versions of type
        const typeMatches = notification.type == NOTIFICATION_TYPES.MEDICATION; // Use loose equality
        const uuidMatches = jsonData && jsonData.userConfigUuid === userConfigUuid;

        // console.log(`[NotificationService] Type match: ${typeMatches}, UUID match: ${uuidMatches}`, {
        //   notificationType: notification.type,
        //   expectedType: NOTIFICATION_TYPES.MEDICATION,
        //   notificationUuid: jsonData?.userConfigUuid,
        //   expectedUuid: userConfigUuid
        // });

        return typeMatches && uuidMatches;
      } catch (error) {
        console.warn(`[NotificationService] Error parsing jsonData for notification ${notification.uuid}:`, error);
        return false;
      }
    });

    // If we found any notifications, mark them as read and deleted
    if (relatedNotifications.length > 0) {
      // console.log(`[NotificationService] Found ${relatedNotifications.length} notifications for taken medication ${userConfigUuid}`);

      for (const notification of relatedNotifications) {
        // console.log(`[NotificationService] Marking notification ${notification.uuid} as read and deleted`);
        await markAsRead(notification.uuid, true);
        await deleteNotification(notification.uuid);
      }
    } else {
      // console.log(`[NotificationService] No pending notifications found for medication ${userConfigUuid}`);
    }
  } catch (error) {
    console.error('[NotificationService] Error handling medication taken event:', error);
  }
};

/**
 * Handle health check completed events
 * @param {Object} event - The event with health check details
 * @param {Function} getNotifications - Function to get all notifications
 * @param {Function} markAsRead - Function to mark notifications as read
 * @param {Function} deleteNotification - Function to delete notifications
 * @returns {Promise<void>}
 */
export const handleHealthCheckCompletedEvent = async (event, getNotifications, markAsRead, deleteNotification) => {
  try {
    // console.log('[NotificationService] Handling health check completed event:', event.detail);
    const { userConfigUuid, userId } = event.detail;

    if (!userConfigUuid || !userId) {
      console.warn('[NotificationService] Missing required data in health check completed event');
      return;
    }

    // Find related notifications for this health check
    const notifications = await getNotifications(userId);
    // console.log(`[NotificationService] Found ${notifications.length} total notifications for user ${userId}`);

    const relatedNotifications = notifications.filter(notification => {
      if (notification.isRead || notification.isDeleted) return false;

      try {
        const jsonData = typeof notification.jsonData === 'string'
          ? JSON.parse(notification.jsonData)
          : notification.jsonData;

        // Compare with both string and number versions of type
        const typeMatches = notification.type == NOTIFICATION_TYPES.HEALTH_CHECK; // Use loose equality
        const uuidMatches = jsonData && jsonData.userConfigUuid === userConfigUuid;

        // console.log(`[NotificationService] Type match: ${typeMatches}, UUID match: ${uuidMatches}`, {
        //   notificationType: notification.type,
        //   expectedType: NOTIFICATION_TYPES.HEALTH_CHECK,
        //   notificationUuid: jsonData?.userConfigUuid,
        //   expectedUuid: userConfigUuid
        // });

        return typeMatches && uuidMatches;
      } catch (error) {
        console.warn(`[NotificationService] Error parsing jsonData for notification ${notification.uuid}:`, error);
        return false;
      }
    });

    // If we found any notifications, mark them as read and deleted
    if (relatedNotifications.length > 0) {
      // console.log(`[NotificationService] Found ${relatedNotifications.length} notifications for completed health check ${userConfigUuid}`);

      for (const notification of relatedNotifications) {
        // console.log(`[NotificationService] Marking notification ${notification.uuid} as read and deleted`);
        await markAsRead(notification.uuid, true);
        await deleteNotification(notification.uuid);
      }
    } else {
      // console.log(`[NotificationService] No pending notifications found for health check ${userConfigUuid}`);
    }
  } catch (error) {
    console.error('[NotificationService] Error handling health check completed event:', error);
  }
}; 